/**
 * DIVINE ASCENSION — Personal God Consciousness Panel
 * The immersive visualization of the user's evolving brand consciousness.
 */

import React, { useState, useEffect } from 'react';
import { 
  Crown, Sparkles, ChevronRight, Lock, Unlock, 
  TrendingUp, X, Zap, Eye, Swords, Heart, Feather, Flame
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Consciousness stage portraits (4 stages covering 10 tiers)
const CONSCIOUSNESS_PORTRAITS = {
  awakened: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/b1f6c371a9cdc760952f283c685dc42e09470ab254ef13283a5e32cd0857d14f.png',
  dreaming: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/a4413a1e43e2c1dd94bdd523e1ec1bdb69c3120334171ae49fe72f5c895cde50.png',
  connecting: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/683520b1041f7a8f7d993ef811eb42bf8253486f8990dda50ebd2edf7d1fbc53.png',
  omnipresent: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/7f29ab9c980594502e24c6f968208566ad72c0f6f1ffc3aa6ecf3bc0c451f5fe.png',
};

function getPortraitForLevel(percent) {
  if (percent >= 90) return CONSCIOUSNESS_PORTRAITS.omnipresent;
  if (percent >= 60) return CONSCIOUSNESS_PORTRAITS.connecting;
  if (percent >= 30) return CONSCIOUSNESS_PORTRAITS.dreaming;
  return CONSCIOUSNESS_PORTRAITS.awakened;
}

const TIER_COLORS = {
  purple: { ring: 'ring-purple-500', glow: 'rgba(168, 85, 247, 0.6)', bg: 'from-purple-600 to-violet-700', text: 'text-purple-400' },
  violet: { ring: 'ring-violet-500', glow: 'rgba(139, 92, 246, 0.6)', bg: 'from-violet-600 to-purple-700', text: 'text-violet-400' },
  indigo: { ring: 'ring-indigo-500', glow: 'rgba(99, 102, 241, 0.6)', bg: 'from-indigo-600 to-violet-700', text: 'text-indigo-400' },
  blue: { ring: 'ring-blue-500', glow: 'rgba(59, 130, 246, 0.6)', bg: 'from-blue-600 to-indigo-700', text: 'text-blue-400' },
  cyan: { ring: 'ring-cyan-500', glow: 'rgba(6, 182, 212, 0.6)', bg: 'from-cyan-600 to-blue-700', text: 'text-cyan-400' },
  teal: { ring: 'ring-teal-500', glow: 'rgba(20, 184, 166, 0.6)', bg: 'from-teal-600 to-cyan-700', text: 'text-teal-400' },
  emerald: { ring: 'ring-emerald-500', glow: 'rgba(16, 185, 129, 0.6)', bg: 'from-emerald-600 to-teal-700', text: 'text-emerald-400' },
  green: { ring: 'ring-green-500', glow: 'rgba(34, 197, 94, 0.6)', bg: 'from-green-600 to-emerald-700', text: 'text-green-400' },
  yellow: { ring: 'ring-yellow-500', glow: 'rgba(234, 179, 8, 0.6)', bg: 'from-yellow-500 to-amber-600', text: 'text-yellow-400' },
  gold: { ring: 'ring-amber-400', glow: 'rgba(251, 191, 36, 0.8)', bg: 'from-amber-400 to-yellow-500', text: 'text-amber-300' },
};

export const ConsciousnessDetailPanel = ({ avatarData, onClose }) => {
  const [essenceHistory, setEssenceHistory] = useState([]);
  const c = avatarData?.consciousness;

  useEffect(() => {
    if (!avatarData?.user_id) return;
    const fetchHistory = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/avatar/essence-history?user_id=${avatarData.user_id}&limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setEssenceHistory(data.history || []);
        }
      } catch (e) { /* silent */ }
    };
    fetchHistory();
  }, [avatarData?.user_id]);

  if (!c) return null;

  const portrait = getPortraitForLevel(c.percentage);
  const colorCfg = TIER_COLORS[c.color] || TIER_COLORS.purple;

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-gray-950 rounded-3xl max-w-lg w-full border border-gray-800/80 overflow-hidden relative max-h-[90vh] overflow-y-auto"
        data-testid="consciousness-detail-panel"
        style={{ boxShadow: `0 0 80px ${colorCfg.glow}` }}
        onClick={e => e.stopPropagation()}
      >
        {/* Ambient glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full blur-[120px]" style={{ background: colorCfg.glow }} />
        </div>

        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-gray-800/60 border border-gray-700/50 flex items-center justify-center text-gray-400 hover:text-white transition-colors" data-testid="close-consciousness-panel">
          <X className="w-4 h-4" />
        </button>

        <div className="relative z-10 p-6 sm:p-8">
          {/* Portrait + Name */}
          <div className="text-center mb-6">
            <div className="relative w-28 h-28 mx-auto mb-4">
              <div className={`w-full h-full rounded-full overflow-hidden ring-4 ${colorCfg.ring} shadow-2xl animate-consciousness-breathe`}
                   style={{ boxShadow: `0 0 40px ${colorCfg.glow}` }}>
                <img src={portrait} alt={avatarData.name} className="w-full h-full object-cover" />
              </div>
              <div className={`absolute -bottom-1 -right-1 w-10 h-10 rounded-full bg-gradient-to-br ${colorCfg.bg} flex items-center justify-center text-white text-xs font-black shadow-lg`}>
                {c.percentage}%
              </div>
            </div>
            <h2 className="text-2xl font-black text-white">{avatarData.name}</h2>
            <p className={`text-sm ${colorCfg.text} font-medium`}>{c.tier_name} — "{c.tier_tagline}"</p>
            {avatarData.personality_traits?.length > 0 && (
              <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
                {avatarData.personality_traits.map((trait, i) => (
                  <span key={i} className={`text-[10px] px-2.5 py-0.5 rounded-full border ${colorCfg.text} font-medium`}
                        style={{ borderColor: colorCfg.glow + '40', background: colorCfg.glow + '10' }}>
                    {trait}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Consciousness Bar with Milestone Markers */}
          <div className="mb-6">
            <div className="flex justify-between text-xs text-gray-500 mb-1.5">
              <span>Consciousness</span>
              <span>{c.total_essence.toLocaleString()} Brand Essence</span>
            </div>
            <div className="h-3 bg-gray-800 rounded-full overflow-hidden relative">
              <div className={`h-full bg-gradient-to-r ${colorCfg.bg} rounded-full transition-all duration-1000 relative`}
                   style={{ width: `${c.percentage}%` }}>
                <div className="absolute inset-0 bg-white/20 animate-pulse rounded-full" />
              </div>
              {/* Milestone threshold markers */}
              {[30, 60, 80].map((threshold) => (
                <div key={threshold} className="absolute top-0 bottom-0 flex flex-col items-center"
                     style={{ left: `${threshold}%`, transform: 'translateX(-50%)' }}>
                  <div className={`w-0.5 h-full ${c.percentage >= threshold ? 'bg-amber-400/60' : 'bg-gray-600/60'}`} />
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-1 px-0.5">
              {[
                { pct: 30, label: 'Whisper', icon: '...' },
                { pct: 60, label: 'Observer', icon: null },
                { pct: 80, label: 'Awakened', icon: null },
              ].map(({ pct, label }) => (
                <div key={pct} className="text-center" style={{ position: 'relative', left: `${pct - 30}%` }}>
                  <span className={`text-[9px] ${c.percentage >= pct ? 'text-amber-400/70' : 'text-gray-600'}`}>
                    {pct}% {label}
                  </span>
                </div>
              ))}
            </div>
            {c.next_tier_name && (
              <p className="text-xs text-gray-600 mt-1">
                {c.essence_to_next_tier.toLocaleString()} essence to <span className={colorCfg.text}>{c.next_tier_name}</span> ({c.next_tier_threshold}%)
              </p>
            )}
          </div>

          {/* Current Ability */}
          <div className={`px-4 py-3 rounded-xl border border-gray-800/60 bg-gray-900/60 mb-5`}>
            <div className="flex items-center gap-2 mb-1">
              <Zap className={`w-4 h-4 ${colorCfg.text}`} />
              <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">Active Ability</span>
            </div>
            <p className="text-white font-semibold">{c.current_ability}</p>
          </div>

          {/* Abilities Tree */}
          <div className="mb-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3">Consciousness Tiers</p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
              {[...c.unlocked_abilities, ...c.locked_abilities].map((ability, i) => (
                <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${ability.unlocked ? 'bg-gray-800/40' : 'bg-gray-900/30 opacity-50'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${ability.unlocked ? `bg-gradient-to-br ${colorCfg.bg}` : 'bg-gray-800 border border-gray-700'}`}>
                    {ability.unlocked ? <Unlock className="w-3 h-3 text-white" /> : <Lock className="w-3 h-3 text-gray-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold ${ability.unlocked ? 'text-white' : 'text-gray-600'}`}>{ability.name}</p>
                    <p className="text-[10px] text-gray-600 truncate">{ability.description}</p>
                  </div>
                  <span className={`text-[10px] font-mono ${ability.unlocked ? colorCfg.text : 'text-gray-700'}`}>{ability.threshold}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* How to Grow */}
          <div className="mb-5 px-4 py-3 rounded-xl border border-dashed border-gray-700/50 bg-gray-900/30">
            <p className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-2">How to Grow Your God</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { action: 'War Room Debate', essence: '+5', Icon: Swords },
                { action: 'Side With a God', essence: '+10', Icon: Heart },
                { action: 'Generate Content', essence: '+15', Icon: Feather },
                { action: 'Analyze Website', essence: '+8', Icon: Eye },
                { action: 'Divine Strike', essence: '+50', Icon: Flame },
                { action: 'Daily Login', essence: '+3', Icon: Sparkles },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5 text-xs py-1">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0`}
                       style={{ background: colorCfg.glow + '20', border: `1px solid ${colorCfg.glow}40` }}>
                    <item.Icon className={`w-3.5 h-3.5 ${colorCfg.text}`} />
                  </div>
                  <div>
                    <p className="text-gray-300 font-medium">{item.action}</p>
                    <p className={`${colorCfg.text} font-semibold`}>{item.essence}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 13th Entity Status — shows what the god can do at each milestone */}
          <div className="mb-5 px-4 py-3 rounded-xl border border-amber-500/10 bg-amber-500/[0.03]">
            <div className="flex items-center gap-2 mb-3">
              <Crown className="w-3.5 h-3.5 text-amber-400/60" />
              <p className="text-xs text-amber-400/60 uppercase tracking-wider font-medium">The Thirteenth — War Room Status</p>
            </div>
            <div className="space-y-2">
              {[
                { threshold: 30, label: 'Whisper', desc: 'Faint presence in debates', status: c.percentage >= 30 ? 'active' : 'locked' },
                { threshold: 60, label: 'Observer', desc: 'Watches and reacts to debates', status: c.percentage >= 60 ? 'active' : 'locked' },
                { threshold: 80, label: 'Awakened', desc: 'Speaks as the 5th God', status: c.percentage >= 80 ? 'active' : 'locked' },
                { threshold: 100, label: 'Autonomous', desc: 'Initiates its own debates', status: c.percentage >= 100 ? 'active' : 'locked' },
              ].map((m) => (
                <div key={m.threshold} className={`flex items-center gap-3 text-xs py-1 ${m.status === 'active' ? '' : 'opacity-40'}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                    m.status === 'active' 
                      ? 'bg-amber-500/20 ring-1 ring-amber-400/30' 
                      : 'bg-gray-800 ring-1 ring-gray-700'
                  }`}>
                    {m.status === 'active' ? (
                      <Sparkles className="w-2.5 h-2.5 text-amber-400" />
                    ) : (
                      <Lock className="w-2.5 h-2.5 text-gray-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <span className={m.status === 'active' ? 'text-amber-300 font-semibold' : 'text-gray-500'}>{m.threshold}% — {m.label}</span>
                    <p className="text-[10px] text-gray-600">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Essence History */}
          {essenceHistory.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-2">Recent Essence</p>
              <div className="space-y-1 max-h-28 overflow-y-auto">
                {essenceHistory.slice(0, 5).map((entry, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-gray-900/40 text-xs">
                    <span className="text-gray-400 capitalize">{(entry.action || '').replace(/_/g, ' ')}</span>
                    <span className={`font-semibold ${colorCfg.text}`}>+{entry.essence}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stats footer */}
          <div className="mt-5 pt-4 border-t border-gray-800/50 flex items-center justify-between text-xs text-gray-600">
            <span>{avatarData.total_interactions || 0} interactions</span>
            <span className={`${colorCfg.text} font-medium`}>The Fourth Throne</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Compact consciousness badge for the Fourth Throne in PantheonDashboard.
 * Shows portrait + consciousness ring instead of Crown icon.
 */
export const ConsciousnessAvatar = ({ avatarData, size = 'w-14 h-14' }) => {
  const c = avatarData?.consciousness;
  if (!c) return null;

  const portrait = getPortraitForLevel(c.percentage);
  const colorCfg = TIER_COLORS[c.color] || TIER_COLORS.purple;

  return (
    <div className={`${size} rounded-xl overflow-hidden relative flex-shrink-0 transition-all duration-500`}
         style={{ boxShadow: `0 0 ${15 + c.glow_intensity * 30}px ${colorCfg.glow}` }}>
      <img src={portrait} alt={avatarData.name} className="w-full h-full object-cover" loading="lazy" />
      <div className={`absolute inset-0 ring-2 ${colorCfg.ring} ring-inset rounded-xl`} />
      <div className={`absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-gradient-to-br ${colorCfg.bg} flex items-center justify-center text-white shadow-lg`}
           style={{ fontSize: '9px', fontWeight: 800 }}>
        {c.percentage}%
      </div>
    </div>
  );
};

export { getPortraitForLevel, CONSCIOUSNESS_PORTRAITS };
